import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api.js";
import type { Scene, StyleReport, StyleFinding } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useModelPicker } from "../ai/ModelPicker.js";

interface Props {
  scene: Scene;
  onClose: () => void;
  onUpdated: (scene: Scene) => void;
}

/**
 * The collapsible scene inspector. These metadata fields deliberately live
 * outside the editor so they never clutter the writing surface; they also feed
 * the AI context builder later. Fields autosave on blur.
 */
export function SceneInspector({ scene, onClose, onUpdated }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const snapshot = (s: Scene) => ({
    status: s.status ?? "",
    storyDate: s.storyDate ?? "",
    storyTime: s.storyTime ?? "",
    goal: s.goal ?? "",
    conflict: s.conflict ?? "",
    outcome: s.outcome ?? "",
    notes: s.notes ?? "",
    summary: s.summary ?? "",
    purpose: s.purpose ?? "",
    sceneFunction: s.sceneFunction ?? "",
    tension: s.tension != null ? String(s.tension) : "",
    setups: s.setups ?? "",
    payoffs: s.payoffs ?? "",
    characterChange: s.characterChange ?? ""
  });
  const [form, setForm] = useState(() => snapshot(scene));

  // Reset when the scene changes.
  useEffect(() => { setForm(snapshot(scene)); }, [scene.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const initial = useMemo(() => snapshot(scene), [scene]);

  const set = <K extends keyof typeof form>(k: K, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const [summarizing, setSummarizing] = useState(false);
  const generateSummary = async () => {
    const choice = await pickModel({ task: "summary" });
    if (!choice) return;
    setSummarizing(true);
    try {
      const { summary } = await api.summarizeScene(scene.id, choice);
      if (summary) {
        setForm((f) => ({ ...f, summary }));
        const { scene: updated } = await api.updateScene(scene.id, { summary });
        onUpdated(updated);
        toast.success("Summary generated.");
      } else {
        toast.info("Nothing to summarize yet — write some text first.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Summary failed");
    } finally {
      setSummarizing(false);
    }
  };

  const persist = async (key: keyof typeof form) => {
    if (form[key] === initial[key]) return;
    const value = key === "tension"
      ? (form.tension.trim() === "" ? null : Math.max(0, Math.min(10, Math.round(Number(form.tension)) || 0)))
      : (form[key] || null);
    try {
      const { scene: updated } = await api.updateScene(scene.id, { [key]: value } as Partial<Scene>);
      onUpdated(updated);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save field");
    }
  };

  const [analyzing, setAnalyzing] = useState(false);
  const analyzeScene = async () => {
    const choice = await pickModel({ task: "summary" });
    if (!choice) return;
    setAnalyzing(true);
    try {
      const { scene: updated, filled } = await api.analyzeScene(scene.id, choice);
      if (updated) {
        setForm(snapshot(updated));
        onUpdated(updated);
      }
      toast.success(filled.length ? `Filled ${filled.length} field${filled.length === 1 ? "" : "s"} (your entries were kept).` : "Nothing new to add — your fields are already set.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <aside className="inspector">
      <header className="inspector-head">
        <div className="row gap-2">
          <Icon name="info" size={16} style={{ color: "var(--accent)" }} />
          <h3 style={{ fontSize: 14 }}>Scene inspector</h3>
        </div>
        <button className="btn btn-icon btn-ghost btn-sm" onClick={onClose} aria-label="Close inspector">
          <Icon name="x" size={15} />
        </button>
      </header>
      <div className="inspector-body">
        <ProseLens sceneId={scene.id} />

        <div className="inspector-section">
          <div className="row gap-2" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <strong style={{ fontSize: 13 }}>Scene purpose</strong>
            <button className="btn btn-sm btn-ghost" disabled={analyzing} onClick={analyzeScene} title="AI fills empty fields only; never overwrites your entries or the prose">
              <Icon name={analyzing ? "clock" : "sparkles"} size={13} className={analyzing ? "spin" : ""} /> Analyze scene
            </button>
          </div>
          <Field label="Purpose" hint="What this scene is for in the story.">
            <textarea className="textarea" rows={2} value={form.purpose}
              onChange={(e) => set("purpose", e.target.value)} onBlur={() => persist("purpose")} />
          </Field>
          <div className="row gap-2">
            <Field label="Function">
              <input className="input" value={form.sceneFunction} placeholder="e.g. Plot advancement"
                onChange={(e) => set("sceneFunction", e.target.value)} onBlur={() => persist("sceneFunction")} />
            </Field>
            <Field label="Tension (0-10)">
              <input className="input" type="number" min={0} max={10} value={form.tension}
                onChange={(e) => set("tension", e.target.value)} onBlur={() => persist("tension")} />
            </Field>
          </div>
          <Field label="Sets up" hint="Setups/questions planted here.">
            <textarea className="textarea" rows={2} value={form.setups}
              onChange={(e) => set("setups", e.target.value)} onBlur={() => persist("setups")} />
          </Field>
          <Field label="Pays off" hint="Earlier setups delivered here.">
            <textarea className="textarea" rows={2} value={form.payoffs}
              onChange={(e) => set("payoffs", e.target.value)} onBlur={() => persist("payoffs")} />
          </Field>
          <Field label="Character change" hint="e.g. Tyler: reluctant → excited">
            <input className="input" value={form.characterChange}
              onChange={(e) => set("characterChange", e.target.value)} onBlur={() => persist("characterChange")} />
          </Field>
        </div>

        <Field label="Status">
          <input className="input" value={form.status} placeholder="e.g. Draft, Needs revision"
            onChange={(e) => set("status", e.target.value)} onBlur={() => persist("status")} />
        </Field>
        <div className="row gap-2">
          <Field label="Story date">
            <input className="input" value={form.storyDate} placeholder="Day 3"
              onChange={(e) => set("storyDate", e.target.value)} onBlur={() => persist("storyDate")} />
          </Field>
          <Field label="Story time">
            <input className="input" value={form.storyTime} placeholder="Dusk"
              onChange={(e) => set("storyTime", e.target.value)} onBlur={() => persist("storyTime")} />
          </Field>
        </div>
        <Field label="Scene goal">
          <textarea className="textarea" rows={2} value={form.goal}
            onChange={(e) => set("goal", e.target.value)} onBlur={() => persist("goal")} />
        </Field>
        <Field label="Conflict">
          <textarea className="textarea" rows={2} value={form.conflict}
            onChange={(e) => set("conflict", e.target.value)} onBlur={() => persist("conflict")} />
        </Field>
        <Field label="Outcome">
          <textarea className="textarea" rows={2} value={form.outcome}
            onChange={(e) => set("outcome", e.target.value)} onBlur={() => persist("outcome")} />
        </Field>
        <Field label="Summary" hint="A short summary used for AI context.">
          <textarea className="textarea" rows={3} value={form.summary}
            onChange={(e) => set("summary", e.target.value)} onBlur={() => persist("summary")} />
          <button className="btn btn-sm btn-ghost" style={{ marginTop: 6 }} disabled={summarizing} onClick={generateSummary}>
            <Icon name={summarizing ? "clock" : "sparkles"} size={13} className={summarizing ? "spin" : ""} /> Generate with Scribe
          </button>
        </Field>
        <Field label="Notes">
          <textarea className="textarea" rows={3} value={form.notes}
            onChange={(e) => set("notes", e.target.value)} onBlur={() => persist("notes")} />
        </Field>
        <p className="faint" style={{ fontSize: 11, marginTop: 4 }}>
          POV character and location selectors arrive with the Characters and Locations modules in Phase 2.
        </p>
      </div>
    </aside>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field grow">
      <label className="label">{label}</label>
      {children}
      {hint && <p className="faint" style={{ fontSize: 10.5, margin: "4px 0 0" }}>{hint}</p>}
    </div>
  );
}

/**
 * Prose Lens — instant, deterministic prose/readability feedback on the current
 * scene (no AI/provider needed). Reflects the last SAVED version of the scene;
 * a Refresh re-fetches after you save edits. Report-only.
 */
function ProseLens({ sceneId }: { sceneId: string }) {
  const [report, setReport] = useState<StyleReport | null>(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.sceneStyle(sceneId).then(setReport).catch(() => setReport(null)).finally(() => setLoading(false));
  };
  // Load when first opened, and reset when the scene changes.
  useEffect(() => { setReport(null); setDetail(false); }, [sceneId]);
  useEffect(() => { if (open && !report) load(); }, [open, report]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="prose-lens">
      <button type="button" className="prose-lens-head" onClick={() => setOpen((o) => !o)}>
        <Icon name={open ? "chevronDown" : "chevronRight"} size={15} />
        <strong style={{ fontSize: 13 }}>Prose Lens</strong>
        <span className="faint" style={{ fontSize: 11 }}>instant prose feedback</span>
      </button>
      {open && (
        <div className="prose-lens-body">
          <div className="row gap-2" style={{ justifyContent: "flex-end", marginBottom: 6 }}>
            <button type="button" className="btn btn-sm btn-ghost" onClick={load} disabled={loading}>
              <Icon name={loading ? "clock" : "layers"} size={12} className={loading ? "spin" : ""} /> {report ? "Refresh" : "Analyze"}
            </button>
          </div>
          {loading && !report && <p className="faint" style={{ fontSize: 12 }}>Analyzing…</p>}
          {report && report.empty && <p className="faint" style={{ fontSize: 12 }}>Write some prose to analyze.</p>}
          {report && !report.empty && (
            <>
              <div className="prose-lens-stats">
                <Stat label="Words" value={report.stats.words.toLocaleString()} />
                <Stat label="Avg sentence" value={`${report.stats.avgSentenceLength}w`} />
                <Stat label="Readability" value={report.readability.label} title={`Grade ${report.readability.gradeLevel} · Flesch ${report.readability.fleschReadingEase}`} />
                <Stat label="Dialogue" value={`${report.stats.dialoguePercent}%`} />
                <Stat label="Read time" value={`${report.stats.readingTimeMin}m`} />
              </div>

              {report.summary.length > 0 && (
                <ul className="prose-lens-summary">
                  {report.summary.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              )}

              <button type="button" className="btn btn-sm btn-ghost" style={{ marginTop: 4 }} onClick={() => setDetail((d) => !d)}>
                {detail ? "Hide details" : "Show details"}
              </button>

              {detail && (
                <div className="prose-lens-detail">
                  <FindingGroup label="Filter words" hint="distance the reader" findings={report.filterWords.items} extra={`${report.filterWords.perThousand}/1k`} />
                  <FindingGroup label="Adverbs" hint="prefer strong verbs" findings={report.adverbs.items} extra={`${report.adverbs.perThousand}/1k`} />
                  {report.adverbs.inDialogueTags.length > 0 && (
                    <FindingGroup label="Adverbs in dialogue tags" findings={report.adverbs.inDialogueTags} />
                  )}
                  <FindingGroup label="Crutch words" findings={report.crutchWords.items} />
                  <FindingGroup label="Weak dialogue tags" hint='vs "said"' findings={report.weakDialogueTags.items} />
                  {report.echoes.length > 0 && (
                    <div className="prose-lens-sub">
                      <div className="prose-lens-sub-head">Close word echoes</div>
                      {report.echoes.slice(0, 8).map((e, i) => (
                        <div key={i} className="prose-lens-echo" title={e.examples.join("  …  ")}>
                          <span className="chip">{e.word}</span>
                          <span className="faint" style={{ fontSize: 11 }}>{e.gapWords} words apart</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {report.sentenceVariety.longest.length > 0 && (
                    <div className="prose-lens-sub">
                      <div className="prose-lens-sub-head">Longest sentences</div>
                      {report.sentenceVariety.longest.map((s, i) => (
                        <div key={i} className="prose-lens-long"><span className="chip">{s.words}w</span> <span className="faint">{s.text}</span></div>
                      ))}
                    </div>
                  )}
                  {report.repeatedOpeners.runs.length > 0 && (
                    <div className="prose-lens-sub">
                      <div className="prose-lens-sub-head">Repeated sentence openers</div>
                      {report.repeatedOpeners.runs.map((r, i) => (
                        <div key={i} className="faint" style={{ fontSize: 11.5 }}>{r.count}× “{r.word}…” — {r.at}</div>
                      ))}
                    </div>
                  )}
                  {report.passive.count > 0 && (
                    <div className="prose-lens-sub">
                      <div className="prose-lens-sub-head">Possible passive voice ({report.passive.count})</div>
                      {report.passive.examples.map((e, i) => <div key={i} className="faint" style={{ fontSize: 11 }}>{e}</div>)}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="prose-lens-stat" title={title}>
      <div className="prose-lens-stat-value">{value}</div>
      <div className="prose-lens-stat-label">{label}</div>
    </div>
  );
}

function FindingGroup({ label, hint, findings, extra }: { label: string; hint?: string; findings: StyleFinding[]; extra?: string }) {
  if (findings.length === 0) return null;
  return (
    <div className="prose-lens-sub">
      <div className="prose-lens-sub-head">
        {label} {hint && <span className="faint" style={{ fontWeight: 400 }}>· {hint}</span>}
        {extra && <span className="faint" style={{ fontWeight: 400, marginLeft: "auto" }}>{extra}</span>}
      </div>
      <div className="prose-lens-chips">
        {findings.map((f) => (
          <span key={f.word} className="chip" title={f.examples.join("  …  ")}>{f.word} <b>{f.count}</b></span>
        ))}
      </div>
    </div>
  );
}
